import { useEffect, useRef } from "react";
import { TimepickerUI } from "timepicker-ui";
import "timepicker-ui/main.css";
import "timepicker-ui/theme-dark.css";
import dayjsTZ from "../../../../utils/dayjs";

/**
 * React wrapper around the imperative `timepicker-ui` clock-face picker.
 *
 * IMPLEMENTATION NOTE — why we render an *empty* <div> and build the
 * <input> imperatively:
 *
 *   `timepicker-ui` wraps / re-parents the <input> when it initialises
 *   (it adds at least one ancestor div for the trigger UI and may
 *   append modal DOM elsewhere). If React owns the <input>, its
 *   reconciler thinks the input still lives where it was originally
 *   rendered, and when the component unmounts (e.g. when the user
 *   flips the Klasik/Orë mode toggle), React's commit phase fires
 *   `parentNode.removeChild(input)` against the wrong parent — which
 *   throws `NotFoundError: ... not a child of this node` and crashes
 *   the whole modal tree.
 *
 *   Rendering only a stable empty <div> from React and creating /
 *   destroying the <input> inside that div via DOM APIs keeps the
 *   library's mutations completely outside React's reconciliation.
 *   On unmount we destroy the picker, then defensively empty the
 *   container — both are no-ops if either side has already cleaned up.
 */
function ClockFaceTimePicker({
  value,
  onChange,
  disabled = false,
  isDarkMode = false,
  disabledHours,
  disabledMinutes,
  placeholder = "HH:MM",
}) {
  const containerRef = useRef(null);
  const pickerRef = useRef(null);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);

  // Keep the latest onChange in a ref so the picker effect below does
  // not re-create the instance every render.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Stable serialisation so the effect only fires when the contents of
  // these arrays actually change (not just their identity).
  const disabledHoursKey = (disabledHours || []).join(",");
  const disabledMinutesKey = (disabledMinutes || []).join(",");

  useEffect(() => {
    if (!containerRef.current || disabled) return undefined;

    const container = containerRef.current;

    // 1. Build the <input> imperatively. React never sees it.
    //    `inputMode="none"` keeps the mobile virtual keyboard from popping
    //    up while still letting timepicker-ui receive native click/focus
    //    events (which `readOnly` is known to interfere with on some
    //    browsers — and which we already saw blocks the picker from
    //    opening in v4).
    const input = document.createElement("input");
    input.type = "text";
    input.className = "shiftc-clockface-input";
    input.placeholder = placeholder;
    input.setAttribute("inputmode", "none");
    input.setAttribute("autocomplete", "off");
    input.setAttribute("spellcheck", "false");
    if (value) input.value = dayjsTZ(value).format("HH:mm");
    container.appendChild(input);
    inputRef.current = input;

    const isMobile =
      typeof window !== "undefined" &&
      (window.matchMedia("(max-width: 600px)").matches ||
        window.matchMedia("(pointer: coarse)").matches);

    // 2. Build the clock options. CRITICAL: timepicker-ui v4 throws an
    //    `INVALID_PARAMETER` error from `checkDisabledValuesOnStart` when
    //    `disabledTime.hours` / `.minutes` are passed as *empty arrays*
    //    (its internal validator returns `undefined` for `[]`, which is
    //    then treated as "invalid"). That error is swallowed by an inner
    //    `try/catch` and the instance is silently marked as destroyed —
    //    so the wrapper renders but click handlers never get attached
    //    and the watch never opens. Only include `disabledTime` when we
    //    actually have entries to disable.
    const dt = {};
    if ((disabledHours || []).length > 0) dt.hours = disabledHours;
    if ((disabledMinutes || []).length > 0) dt.minutes = disabledMinutes;

    const clockOptions = {
      type: "24h",
      incrementMinutes: 1,
      currentTime: value
        ? { time: dayjsTZ(value).toDate(), updateInput: true }
        : { time: new Date(), updateInput: true },
    };
    if (Object.keys(dt).length > 0) clockOptions.disabledTime = dt;

    // 3. Attach timepicker-ui to that input.
    const picker = new TimepickerUI(input, {
      clock: clockOptions,
      ui: {
        theme: isDarkMode ? "dark" : "basic",
        mobile: isMobile,
        mode: "clock",
        enableSwitchIcon: true,
        editable: false,
        backdrop: true,
      },
      labels: {
        ok: "OK",
        cancel: "Anulo",
        time: "Zgjidh orën",
        mobileTime: "Vendos orën",
        mobileHour: "Ora",
        mobileMinute: "Minuta",
      },
      behavior: {
        focusInputAfterClose: false,
      },
      callbacks: {
        onConfirm: (data) => {
          const h = Number(data?.hour ?? 0);
          const m = Number(data?.minutes ?? 0);
          if (Number.isNaN(h) || Number.isNaN(m)) return;
          const next = dayjsTZ()
            .set("hour", h)
            .set("minute", m)
            .set("second", 0);
          onChangeRef.current?.(next);
        },
      },
    });

    picker.create();
    pickerRef.current = picker;

    return () => {
      // 4. Tear down the picker first. Close the overlay (if any) before
      // destroying so the library can clean its own body-level modal
      // nodes; then destroy releases listeners and DOM.
      try {
        picker.close(false);
      } catch (_) {
        // overlay may not be open — ignore
      }
      try {
        picker.destroy({ keepInputValue: true });
      } catch (_) {
        // best-effort: library may have already cleaned up
      }
      pickerRef.current = null;

      // 5. Empty our container defensively. By this point timepicker-ui
      // may have wrapped the <input> in extra ancestors; removing
      // whatever remains as a direct child of our React-owned <div>
      // is always safe and keeps React's reconciler happy.
      try {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      } catch (_) {
        // ignored
      }
      inputRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, isDarkMode, disabledHoursKey, disabledMinutesKey, placeholder]);

  // Push parent-driven value changes into the picker without
  // re-creating the instance.
  useEffect(() => {
    if (!pickerRef.current || !value) return;
    try {
      pickerRef.current.setValue(dayjsTZ(value).format("HH:mm"), true);
      // Mirror the value into our DOM-owned input as well, so the
      // visible text stays in sync even if the library does not.
      if (inputRef.current) {
        inputRef.current.value = dayjsTZ(value).format("HH:mm");
      }
    } catch (_) {
      // ignored
    }
  }, [value]);

  return (
    <div
      ref={containerRef}
      className="shiftc-clockface-container"
      aria-disabled={disabled || undefined}
    />
  );
}

export default ClockFaceTimePicker;
